import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EditSignalRequest, EditSignalResponse } from "./types";
import { getSignalQueryKey } from "./useGetSignal";
import { getSignalsQueryKeyPrefix } from "./useGetSignals";

import { ENDPOINTS } from "@/api/application-api";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface EditSignalParams extends EditSignalRequest {
  workspaceId: number;
  signalId: number;
  userToken: string;
  successMessage?: string;
}

export const useEditSignal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      signalId,
      userToken,
      successMessage: _successMessage,
      ...payload
    }: EditSignalParams): Promise<EditSignalResponse> => {
      const response = await fetch(ENDPOINTS.signals.edit(workspaceId, signalId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });

      return handleResponse(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getSignalsQueryKeyPrefix(variables.workspaceId),
      });
      queryClient.invalidateQueries({
        queryKey: getSignalQueryKey(variables.workspaceId, variables.signalId),
      });
      displayToast(variables.successMessage ?? "Signal saved", ToastType.success);
    },
    onError: (error: Error) => {
      displayErrorToast(error.message);
    },
  });
};
